/**
 * BriefingPage.jsx — Composant générique premium mobile-first "Briefing avant sortie moto".
 * Utilisé par /briefing-circulation (AM) et /briefing-a2 (moto A2).
 * Reçoit briefing + briefingQuiz en props (7 branches + 5 réflexes + mini QCM).
 */
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import {
  ArrowLeft, Eye, Users, AlertTriangle, CornerDownRight, Route, Redo2,
  Radio, Crown, Bike, MapPin, Gauge, Shield, Brain, MoveHorizontal, Check, XIcon,
  Sparkles, Trophy, ChevronDown, ChevronRight, RefreshCw, QrCode, X, Share2, Download,
} from 'lucide-react';
import useSeo from '../hooks/useSeo';

const ICONS = { Eye, Users, AlertTriangle, CornerDownRight, Route, Redo2, Radio, Crown, Bike, MapPin, Gauge, Shield, Brain, MoveHorizontal };

const COLOR_MAP = {
  blue:    { bg: 'from-blue-600/20 to-blue-950/20',       border: 'border-blue-500/40',    text: 'text-blue-300',    ring: 'ring-blue-500/40',    solid: 'bg-blue-600' },
  emerald: { bg: 'from-emerald-600/20 to-emerald-950/20', border: 'border-emerald-500/40', text: 'text-emerald-300', ring: 'ring-emerald-500/40', solid: 'bg-emerald-600' },
  orange:  { bg: 'from-orange-500/20 to-orange-950/20',   border: 'border-orange-500/40',  text: 'text-orange-300',  ring: 'ring-orange-500/40',  solid: 'bg-orange-600' },
  purple:  { bg: 'from-purple-600/20 to-purple-950/20',   border: 'border-purple-500/40',  text: 'text-purple-300',  ring: 'ring-purple-500/40',  solid: 'bg-purple-600' },
  sky:     { bg: 'from-sky-600/20 to-sky-950/20',         border: 'border-sky-500/40',     text: 'text-sky-300',     ring: 'ring-sky-500/40',     solid: 'bg-sky-600' },
  lime:    { bg: 'from-lime-600/20 to-lime-950/20',       border: 'border-lime-500/40',    text: 'text-lime-300',    ring: 'ring-lime-500/40',    solid: 'bg-lime-600' },
  red:     { bg: 'from-red-600/20 to-red-950/20',         border: 'border-red-500/40',     text: 'text-red-300',     ring: 'ring-red-500/40',     solid: 'bg-red-600' },
};

/* ─────────────────────────────────────────────────────────────────
   Carte pliable pour chaque branche
   ───────────────────────────────────────────────────────────────── */
function Branch({ b, expanded, onToggle }) {
  const c = COLOR_MAP[b.color] || COLOR_MAP.red;
  const Icon = ICONS[b.icon] || Sparkles;

  return (
    <div
      className={`rounded-2xl border-2 ${c.border} bg-gradient-to-br ${c.bg} overflow-hidden`}
      data-testid={`branch-${b.num}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-3 text-left active:scale-[0.98] transition"
        aria-expanded={expanded}
        data-testid={`branch-toggle-${b.num}`}
      >
        <div className={`flex-shrink-0 w-11 h-11 rounded-full ${c.solid} text-white flex items-center justify-center text-lg font-black shadow-lg`}>
          {b.num}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] font-black uppercase tracking-widest ${c.text} mb-0.5`}>
            {b.subtitle}
          </div>
          <div className="text-lg font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {b.title}
          </div>
        </div>
        <Icon className={`w-5 h-5 ${c.text} flex-shrink-0`} />
        <ChevronDown className={`w-4 h-4 ${c.text} flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 animate-in fade-in duration-200">
          {/* Points génériques (branche 1) */}
          {b.points && (
            <ul className="space-y-2">
              {b.points.map((p, i) => {
                const PIcon = ICONS[p.icon] || Sparkles;
                return (
                  <li key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-black/25 border border-white/8">
                    <PIcon className={`w-4 h-4 ${c.text} flex-shrink-0`} />
                    <span className="text-sm text-foreground/95">{p.text}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Regard / Anticipation (branche 2) */}
          {b.lookZones && (
            <>
              <div className="p-3 rounded-lg bg-black/25 border border-white/8">
                <div className={`text-[10px] font-black uppercase tracking-widest ${c.text} mb-2`}>Je regarde</div>
                <ul className="space-y-1">
                  {b.lookZones.map((z, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground/95">
                      <span className={`w-1.5 h-1.5 rounded-full ${c.solid}`} /> {z}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-3 rounded-lg bg-black/25 border border-white/8">
                <div className={`text-[10px] font-black uppercase tracking-widest ${c.text} mb-2`}>Méthode</div>
                <div className="flex flex-wrap gap-1.5">
                  {b.method.map((m, i) => (
                    <React.Fragment key={i}>
                      <span className={`px-2 py-1 rounded-md ${c.solid} text-white text-xs font-black uppercase tracking-widest`}>{m}</span>
                      {i < b.method.length - 1 && <ChevronRight className={`w-4 h-4 ${c.text} self-center`} />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Virages (branche 3) */}
          {b.subCards && (
            <>
              {b.subCards.map((sc, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-lg border ${sc.tone === 'ok' ? 'border-emerald-500/40 bg-emerald-500/8' : 'border-orange-500/40 bg-orange-500/8'}`}
                >
                  <div className={`text-xs font-black uppercase tracking-widest mb-2 ${sc.tone === 'ok' ? 'text-emerald-300' : 'text-orange-300'}`}>
                    {sc.title}
                  </div>
                  <ul className="space-y-1">
                    {sc.bullets.map((bl, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-foreground/95">
                        <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${sc.tone === 'ok' ? 'text-emerald-400' : 'text-orange-400'}`} />
                        <span>{bl}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {b.consigneForte && (
                <div className="p-3 rounded-lg bg-red-600 text-white border border-red-400">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <div className="text-[10px] font-black uppercase tracking-widest">Consigne forte</div>
                  </div>
                  <div className="text-sm font-black mt-1 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {b.consigneForte}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Trajectoire (branche 4) — keyPhrase toujours affichée, allows/adapts seulement si définis (AM) */}
          {b.keyPhrase && (
            <>
              <div className="p-3 rounded-lg bg-black/30 border border-purple-500/40">
                <p className="text-sm font-bold text-purple-200 italic" style={{ fontFamily: "'Crimson Text', Georgia, serif" }}>
                  {b.keyPhrase}
                </p>
              </div>
              {b.allows && b.adapts && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 rounded-lg bg-black/25 border border-white/8">
                    <div className={`text-[10px] font-black uppercase tracking-widest ${c.text} mb-1.5`}>Elle permet de</div>
                    <ul className="space-y-1">
                      {b.allows.map((a, i) => (
                        <li key={i} className="text-sm font-black text-foreground/95">{a}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-3 rounded-lg bg-black/25 border border-white/8">
                    <div className={`text-[10px] font-black uppercase tracking-widest ${c.text} mb-1.5`}>Elle s&apos;adapte à</div>
                    <ul className="space-y-1">
                      {b.adapts.map((a, i) => (
                        <li key={i} className="text-xs text-foreground/90">• {a}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Giratoire (branche 5) */}
          {b.phases && (
            <div className="space-y-2">
              {b.phases.map((ph, i) => (
                <div key={i} className="p-3 rounded-lg bg-black/25 border border-white/8">
                  <div className={`text-[10px] font-black uppercase tracking-widest ${c.text} mb-2`}>
                    {ph.title}
                  </div>
                  <ul className="space-y-1">
                    {ph.steps.map((s, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-foreground/95">
                        <Check className={`w-3.5 h-3.5 ${c.text} flex-shrink-0`} /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Circulation en groupe (branche 6) */}
          {b.roles && (
            <div className="space-y-2">
              {b.roles.map((r, i) => {
                const RIcon = ICONS[r.icon] || Users;
                return (
                  <div key={i} className="p-3 rounded-lg bg-black/25 border border-white/8">
                    <div className="flex items-center gap-2 mb-1.5">
                      <RIcon className={`w-4 h-4 ${c.text}`} />
                      <div className="text-sm font-black uppercase tracking-widest text-foreground">{r.role}</div>
                    </div>
                    <div className={`text-[10px] font-black uppercase tracking-widest ${c.text} mb-2`}>{r.motto}</div>
                    <ul className="space-y-0.5">
                      {r.bullets.map((bl, j) => (
                        <li key={j} className="text-sm text-foreground/95">• {bl}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              {b.regle && (
                <div className="p-3 rounded-lg bg-amber-600 text-white border border-amber-400">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    <div className="text-[10px] font-black uppercase tracking-widest">Règle groupe</div>
                  </div>
                  <div className="text-sm font-black mt-1 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {b.regle}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Communication / Perte du groupe (branche 7) */}
          {b.communication && (
            <div className="space-y-2">
              <div className="p-3 rounded-lg bg-black/25 border border-white/8">
                <div className="flex items-center gap-2 mb-1.5">
                  <Radio className={`w-4 h-4 ${c.text}`} />
                  <div className="text-sm font-black uppercase tracking-widest">Communication</div>
                </div>
                <div className={`text-[10px] font-black uppercase tracking-widest ${c.text} mb-2`}>{b.communication.motto}</div>
                <div className={`text-[10px] font-black uppercase tracking-widest ${c.text} mb-1`}>Je signale</div>
                <ul className="space-y-0.5">
                  {b.communication.signals.map((s, i) => (
                    <li key={i} className="text-sm text-foreground/95">• {s}</li>
                  ))}
                </ul>
              </div>
              {b.perte && (
                <div className="p-3 rounded-lg bg-red-950/50 border border-red-500/50">
                  <div className="text-[10px] font-black uppercase tracking-widest text-red-300 mb-1">
                    {b.perte.title}
                  </div>
                  <p className="text-sm font-black text-red-100 mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {b.perte.headline}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {b.perte.steps.map((s, i) => (
                      <React.Fragment key={i}>
                        <span className="px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-black uppercase tracking-widest">{s}</span>
                        {i < b.perte.steps.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-red-300 self-center" />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Consigne finale de branche */}
          {b.consigne && (
            <div className={`p-3 rounded-lg border ${c.border} bg-black/40`}>
              <div className={`text-[10px] font-black uppercase tracking-widest ${c.text} mb-1`}>Consigne</div>
              <p className="text-sm font-bold text-foreground italic" style={{ fontFamily: "'Crimson Text', Georgia, serif" }}>
                {b.consigne}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Mini QCM d'ancrage — 5 questions
   ───────────────────────────────────────────────────────────────── */
function MiniQuiz({ onClose, briefingQuiz }) {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const q = briefingQuiz.questions[current];
  const ans = answers[current];
  const total = briefingQuiz.questions.length;
  const correctCount = useMemo(
    () => Object.values(answers).filter((a) => a.correct).length,
    [answers],
  );

  const submit = (i) => {
    const correct = q.answer === i;
    setAnswers((prev) => ({ ...prev, [current]: { pick: i, correct, correctAnswer: q.answer, explain: q.explain } }));
  };
  const next = () => {
    if (current + 1 >= total) setSubmitted(true);
    else setCurrent((c) => c + 1);
  };

  if (submitted) {
    const pct = Math.round((correctCount / total) * 100);
    return (
      <div className="rounded-2xl border-2 border-red-500/40 bg-gradient-to-br from-red-950/40 to-black/40 p-5 text-center" data-testid="briefing-quiz-result">
        <Trophy className={`w-10 h-10 mx-auto mb-2 ${pct >= 80 ? 'text-yellow-400' : pct >= 60 ? 'text-slate-300' : 'text-orange-400'}`} />
        <div className="text-4xl font-black tabular-nums" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {correctCount}<span className="text-xl text-muted-foreground">/{total}</span>
        </div>
        <div className="text-sm text-muted-foreground mt-1">
          {pct >= 80 ? 'Prêt à rouler — tes réflexes sont là.' : pct >= 60 ? 'Presque prêt — relis les branches concernées.' : 'À revoir avant de partir. Reprends les 7 branches.'}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <Button onClick={() => { setSubmitted(false); setCurrent(0); setAnswers({}); }} variant="outline" className="flex-1" data-testid="briefing-quiz-restart">
            <RefreshCw className="w-4 h-4 mr-1" /> Rejouer
          </Button>
          <Button onClick={onClose} className="flex-1 bg-red-600 hover:bg-red-500 text-white" data-testid="briefing-quiz-close">
            Retour au briefing
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-red-500/40 bg-gradient-to-br from-red-950/40 to-black/40 p-4" data-testid="briefing-quiz">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-black uppercase tracking-widest text-red-300">QCM · Ancrage avant départ</div>
        <div className="text-xs font-black tabular-nums text-foreground">{current + 1}/{total}</div>
      </div>
      <div className="h-1 rounded-full bg-white/10 mb-4 overflow-hidden">
        <div className="h-full bg-red-500 transition-all" style={{ width: `${((current + 1) / total) * 100}%` }} />
      </div>
      <p className="text-base font-black leading-snug mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        {q.q}
      </p>
      <div className="space-y-2">
        {q.choices.map((choice, i) => {
          const has = ans !== undefined;
          const isRight = has && ans.correctAnswer === i;
          const isWrong = has && ans.pick === i && !ans.correct;
          return (
            <button
              key={i}
              type="button"
              onClick={() => !has && submit(i)}
              disabled={has}
              className={`w-full text-left p-3 rounded-xl border-2 flex items-center gap-3 text-sm transition ${
                !has ? 'border-white/10 bg-black/25 hover:border-red-500/50 active:scale-[0.98]' :
                isRight ? 'border-emerald-500 bg-emerald-500/15' :
                isWrong ? 'border-orange-500 bg-orange-500/15' :
                'border-white/10 bg-black/20 opacity-50'
              }`}
              data-testid={`briefing-quiz-choice-${i}`}
            >
              <span className={`flex-shrink-0 w-7 h-7 rounded-lg font-black flex items-center justify-center text-sm ${
                isRight ? 'bg-emerald-500 text-white' : isWrong ? 'bg-orange-500 text-white' : 'bg-white/10 text-foreground/80'
              }`}>{['A','B','C','D'][i]}</span>
              <span className="flex-1 text-foreground/95">{choice}</span>
              {isRight && <Check className="w-4 h-4 text-emerald-400" strokeWidth={3} />}
              {isWrong && <XIcon className="w-4 h-4 text-orange-400" strokeWidth={3} />}
            </button>
          );
        })}
      </div>
      {ans && (
        <div className="mt-3 p-3 rounded-lg bg-black/30 border border-white/10">
          <div className={`text-[10px] font-black uppercase tracking-widest mb-1 ${ans.correct ? 'text-emerald-300' : 'text-orange-300'}`}>
            {ans.correct ? '✓ Bonne réponse' : '✗ Pas cette fois'}
          </div>
          <p className="text-xs text-foreground/90 leading-relaxed">{ans.explain}</p>
          <Button onClick={next} size="sm" className="w-full mt-3 bg-red-600 hover:bg-red-500 text-white" data-testid="briefing-quiz-next">
            {current + 1 >= total ? 'Voir mon score' : 'Question suivante'}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Page principale
   ───────────────────────────────────────────────────────────────── */
export default function BriefingPage({ briefing, briefingQuiz, heroEmoji = "🏍️", accentTone = "red", reflexesLabel = "Les 5 réflexes du motard" }) {
  const [expanded, setExpanded] = useState(1);
  const [quizOpen, setQuizOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  useSeo({
    title: `${briefing.title} — Code 2 Roues`,
    description: briefing.subtitle,
  });

  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://code2roues.fr/briefing-circulation';
  const toggle = (n) => setExpanded((cur) => (cur === n ? null : n));

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: briefing.title,
          text: briefing.subtitle,
          url: shareUrl,
        });
      } catch {
        /* utilisateur a annulé */
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Lien copié dans le presse-papier');
      } catch {
        alert(shareUrl);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground pb-16" data-testid="briefing-page">
      {/* Topbar sticky */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur">
        <div className="max-w-2xl mx-auto px-3 py-3 flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" data-testid="briefing-back">
            <Link to="/" reloadDocument><ArrowLeft className="w-4 h-4" /></Link>
          </Button>
          <div className="flex-1 text-center">
            <div className="text-[9px] font-black uppercase tracking-[0.22em] text-red-400">Briefing</div>
            <div className="text-sm font-black leading-tight truncate">{briefing.title}</div>
          </div>
          <Button onClick={handleShare} variant="ghost" size="sm" data-testid="briefing-share-btn" title="Partager le lien">
            <Share2 className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => setQrOpen(true)}
            size="sm"
            className="bg-red-600 hover:bg-red-500 text-white"
            data-testid="briefing-qr-btn"
            title="Afficher le QR code à projeter"
          >
            <QrCode className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">QR</span>
          </Button>
        </div>
      </div>

      {/* MODALE QR CODE plein écran — Optimisée mobile-to-mobile (partage aux élèves sur le terrain) */}
      {qrOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200"
          onClick={() => setQrOpen(false)}
          data-testid="briefing-qr-modal"
        >
          <button
            type="button"
            onClick={() => setQrOpen(false)}
            className="absolute top-3 right-3 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
            data-testid="briefing-qr-close"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl p-5 sm:p-8 max-w-sm w-full text-center animate-in zoom-in-95 duration-300"
          >
            {/* Instruction claire */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-600 text-white text-[10px] font-black uppercase tracking-widest mb-3">
              <Bike className="w-3 h-3" /> Sur le parking
            </div>
            <h2
              className="text-xl sm:text-2xl font-black text-gray-900 leading-tight mb-1"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Fais scanner ce QR<br />par tes élèves
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mb-4">
              Ils ouvrent l&apos;appareil photo, visent le code, ils reçoivent le briefing sur leur téléphone.
            </p>

            {/* QR code */}
            <div className="inline-block bg-white p-3 rounded-2xl border-4 border-gray-900 shadow-xl">
              <QRCodeSVG value={shareUrl} size={220} level="M" fgColor="#111827" />
            </div>

            {/* Lien texte fallback */}
            <div className="mt-4">
              <div className="text-[10px] font-black uppercase tracking-[0.24em] text-gray-500 mb-1">
                Ou saisis directement
              </div>
              <div className="text-sm sm:text-base font-mono font-black text-gray-900 break-all">
                {shareUrl.replace(/^https?:\/\//, '')}
              </div>
            </div>

            {/* CTA partage natif (SMS / WhatsApp / autre) */}
            <button
              type="button"
              onClick={handleShare}
              className="w-full mt-5 rounded-xl bg-red-600 hover:bg-red-500 active:scale-[0.98] text-white font-black uppercase tracking-widest py-3.5 text-sm inline-flex items-center justify-center gap-2 transition-all"
              data-testid="briefing-qr-share"
            >
              <Share2 className="w-4 h-4" /> Partager par SMS / WhatsApp
            </button>

            <button
              type="button"
              onClick={() => setQrOpen(false)}
              className="w-full mt-2 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-800"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      <main className="max-w-2xl mx-auto px-3 py-5 space-y-5">
        {/* HERO */}
        <header className="text-center space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-[10px] font-black uppercase tracking-widest text-red-300">
            <Bike className="w-3 h-3" /> Avant de partir
          </div>
          <h1
            className="text-3xl sm:text-4xl font-black leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {heroEmoji} {briefing.title}
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {briefing.subtitle}
          </p>
          <div className="p-3 rounded-xl border border-red-500/30 bg-gradient-to-br from-red-500/10 to-transparent">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-1">Fil conducteur</div>
            <div className="text-sm font-black text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {briefing.mantra}
            </div>
          </div>
        </header>

        {/* CARTE MENTALE — lien discret uniquement, pas d'affichage inline */}
        {briefing.posterUrl && (
          <a
            href={briefing.posterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-2xl border-2 border-amber-500/40 bg-gradient-to-r from-amber-600/15 to-transparent p-3 sm:p-4 hover:border-amber-500/70 active:scale-[0.99] transition-all"
            data-testid="briefing-poster-link"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-amber-500/20 border-2 border-amber-500/50 flex items-center justify-center">
              <Download className="w-5 h-5 text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-300 mb-0.5">
                Carte mentale imprimable · A3
              </div>
              <div className="text-sm font-black leading-tight text-foreground">
                Ouvrir / télécharger la carte
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-amber-300 flex-shrink-0" />
          </a>
        )}

        {/* CTA Quiz d'ancrage */}
        {!quizOpen && (
          <button
            type="button"
            onClick={() => setQuizOpen(true)}
            className="w-full p-4 rounded-2xl bg-gradient-to-br from-red-600 to-red-700 text-white active:scale-[0.98] transition flex items-center gap-3 shadow-lg"
            data-testid="briefing-quiz-launch"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-[10px] font-black uppercase tracking-widest opacity-90">Ancrage · 5 questions</div>
              <div className="text-base font-black leading-tight">Tester mes réflexes avant de partir</div>
            </div>
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {quizOpen && <MiniQuiz onClose={() => setQuizOpen(false)} briefingQuiz={briefingQuiz} />}

        {/* 7 BRANCHES */}
        <section className="space-y-3" data-testid="branches-list">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 text-center">
            7 branches · touche pour ouvrir
          </div>
          {briefing.branches.map((b) => (
            <Branch key={b.num} b={b} expanded={expanded === b.num} onToggle={() => toggle(b.num)} />
          ))}
        </section>

        {/* 5 RÉFLEXES DU MOTARD */}
        <section
          className="rounded-2xl border-2 border-red-500/40 bg-gradient-to-br from-red-950/30 to-black/40 p-4"
          data-testid="reflexes-list"
        >
          <div className="text-center mb-4">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-1">
              À retenir
            </div>
            <h2 className="text-xl font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {reflexesLabel}
            </h2>
          </div>
          <div className="space-y-2">
            {briefing.reflexes.map((r) => {
              const c = COLOR_MAP[r.color] || COLOR_MAP.red;
              const RIcon = ICONS[r.icon] || Sparkles;
              return (
                <div key={r.num} className={`p-3 rounded-xl border ${c.border} bg-black/30 flex items-center gap-3`} data-testid={`reflex-${r.num}`}>
                  <div className={`flex-shrink-0 w-9 h-9 rounded-full ${c.solid} text-white flex items-center justify-center font-black`}>
                    {r.num}
                  </div>
                  <RIcon className={`w-5 h-5 ${c.text} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black uppercase tracking-wide text-foreground">{r.title}</div>
                    <div className="text-xs text-muted-foreground leading-snug">{r.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Signature */}
        <div className="text-center text-[10px] text-muted-foreground pt-2">
          {briefing.slogan}
        </div>
      </main>
    </div>
  );
}
